<template>
	<div class="resource-planning-split-toggle">
		<SuiButton
			v-tooltip="$t('resource_planning_split_booking')"
			class="resource-planning-split-toggle__button"
			:class="{
				'resource-planning-split-toggle__button--active': isActive,
			}"
			:aria-label="$t('resource_planning_split_booking')"
			@click="$emit('toggle')"
		>
			<SuiIcon
				name="icon-scissors-16"
				class="resource-planning-split-toggle__icon"
			/>
		</SuiButton>

		<div
			v-if="isActive"
			class="resource-planning-split-toggle__badge"
			aria-hidden="true"
		>
			<SuiIcon name="icon-check-filled-16" />
		</div>
	</div>
</template>

<script setup lang="ts">
import { watch, onBeforeUnmount } from 'vue';
import { SuiButton, SuiIcon, SuiMessageTypeType } from '@developers/scoro-ui';
import { useTranslation } from '@/composables';
import { KeyboardKey } from '@/constants/constants';
import { useMessage } from '@/features/Message';
import { useMessagesStore } from '@/features/Message/stores/messages';
import { SPLIT_MODE_MESSAGE_ID } from './constants';

const { isActive = false } = defineProps<{
	isActive?: boolean;
}>();

const emit = defineEmits<{
	(e: 'toggle'): void;
}>();

const { t } = useTranslation();
const { addMessage, removeMessage } = useMessage();
const messagesStore = useMessagesStore();

const handleKeydown = (e: KeyboardEvent) => {
	if (e.key === KeyboardKey.ESC) {
		emit('toggle');
	}
};

watch(
	() => isActive,
	(active) => {
		if (active) {
			addMessage({
				id: SPLIT_MODE_MESSAGE_ID,
				type: SuiMessageTypeType.Info,
				message: t('resource_planning_split_booking_message'),
				showUndo: true,
				undoLabel: t('resource_planning_split_booking_done'),
				onUndo: () => emit('toggle'),
			});
			document.addEventListener('keydown', handleKeydown);
		} else {
			removeMessage(SPLIT_MODE_MESSAGE_ID);
			document.removeEventListener('keydown', handleKeydown);
		}
	},
	{ immediate: true }
);

watch(
	() =>
		messagesStore.messages.some((msg) => msg.id === SPLIT_MODE_MESSAGE_ID),
	(exists) => {
		if (!exists && isActive) {
			emit('toggle');
		}
	}
);

onBeforeUnmount(() => {
	document.removeEventListener('keydown', handleKeydown);
});
</script>

<style lang="scss" scoped>
.resource-planning-split-toggle {
	$parent: &;

	position: relative;
	display: inline-flex;

	#{$parent}__icon {
		margin: 0;
	}

	&__button {
		--sui-button-text-color: #{$neutral-70};
		--sui-button-hover-text-color: #{$neutral-70};

		background: $neutral-10;
		padding: 0.75rem;
		border: 0.25rem solid transparent;

		&--active {
			--sui-button-text-color: #{$blue-50};
			--sui-button-hover-text-color: #{$blue-60};
			--sui-button-hover-background-color: #{$blue-10};

			background: $blue-10;
			border: 0.25rem solid $blue-30;
		}
	}

	&:hover &__badge {
		--sui-icon-fill: #{$blue-60};
	}

	&__badge {
		--sui-icon-fill: #{$blue-50};

		position: absolute;
		top: -0.75rem;
		right: -0.75rem;
	}
}
</style>
